import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {
  AddToBasketSectionMinimal,
  BasketFlyout,
  CouponHintMinimal,
  DiscountBanner,
  GoBackMinimal,
  MerchantInfoWithLegal,
  MiniADPDialog,
  MiniADPDropdown,
  PageMetaData,
  PageMetaDataJsonLd,
  PriceAndBrandMinimal,
  ProductSelectionMinimal,
  ProductTileImageSlider,
  ProminentBannerSectionMinimal,
  SizeSelectionOpenedStateKey,
  UnlockCouponSuccessHint,
  useBasketFlyoutOpenedState,
  useIsCouponsEnabled,
  useProductIdFromSlug,
  useSelectedSizeState,
} from '@aboutyou/logic-components';
import {
  GenericIconName,
  Icon,
  ImageType,
  ModalDialog,
  useLockBodyScroll,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {StrictMode, memo, useState} from 'react';
import {CategoryStreamBackground} from './CategoryStreamBackground';

const MemoPageMetaData = memo(PageMetaData);
const MemoPageMetaDataSectionJsonLd = memo(PageMetaDataJsonLd);
const MemoGoBack = memo(GoBackMinimal);
const MemoPriceAndBrandMinimal = memo(PriceAndBrandMinimal);
const MemoProminentBannerSectionMinimal = memo(ProminentBannerSectionMinimal);
const MemoProductSelectionMinimal = memo(ProductSelectionMinimal);
const MemoAddToBasketSectionMinimal = memo(AddToBasketSectionMinimal);
const MemoProductTileImageSlider = memo(ProductTileImageSlider);
const MemoMerchantInfo = memo(MerchantInfoWithLegal);

export const QuickViewArticle: React.FC<{isQuickView?: boolean}> = ({
  isQuickView,
}) => {
  const {selectedSize, setSelectedSize} = useSelectedSizeState();
  const productId = useProductIdFromSlug();

  const {open: openBasketFlyout} = useBasketFlyoutOpenedState();
  const hydrationDone = useHydrationDone();
  const [statusSuccessSections, setStatusSuccessSections] = useState<
    AddItemResponseStatus_Success_Section[]
  >([]);
  const [previewImage, setPreviewImage] = useState<ImageType>();

  const isCouponsEnabled = useIsCouponsEnabled() && hydrationDone;

  useLockBodyScroll({lockScroll: true});

  const [freeDeliverySectionData, setFreeDeliverySectionData] = useState<
    FreeDeliverySection | undefined
  >();

  return (
    <StrictMode>
      <MemoPageMetaData />
      <MemoPageMetaDataSectionJsonLd />
      <MiniADPDialog
        skipAnimation
        isOpen
        isQuickView={isQuickView}
        closeButton={
          <StyledGoBack
            productId={productId!}
            data-testid="closeButton"
            Icon={CloseButtonIcon}
            addFirstProductId
          />
        }
        imageSlider={
          <MemoProductTileImageSlider
            redirectOnClick
            showThumbnails
            productId={productId!}
            previewImage={previewImage}
          />
        }
        priceAndBrand={
          <>
            <MemoPriceAndBrandMinimal
              brandLogoAlignment="left"
              selectedSize={selectedSize}
              productId={productId!}
              showActiveCouponBadge
              PriceLegalInfoLayoutComponent={ModalDialog}
            />
            {isCouponsEnabled && <CouponHintMinimal productId={productId!} />}
          </>
        }
        prominentBanner={null}
        productSelection={
          <MemoProductSelectionMinimal
            disablePreviewImage
            ColorSelectionLayoutComponent={MiniADPDropdown}
            productId={productId!}
            setPreviewImage={setPreviewImage}
          />
        }
        sizeSelection={
          <>
            <MemoAddToBasketSectionMinimal
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              showPriceInSizeSelectionHeader={false}
              showSizeAdvisor={true}
              SizeReminderLayoutComponent={ModalDialog}
              SizeSelectionLayoutComponent={MiniADPDropdown}
              showWishlistButton={false}
              sizeSelectionStateManagerKey={
                SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
              }
              productId={productId!}
              onAddToBasketSuccess={({
                statusSuccessSections,
                freeDeliverySection,
              }) => {
                openBasketFlyout(productId!);
                setStatusSuccessSections(statusSuccessSections);
                setFreeDeliverySectionData(freeDeliverySection);

                // This is a temporary fix until the root cause will be fixed with https://aboutyou.atlassian.net/browse/AYFEP-2859
                setTimeout(() => openBasketFlyout(productId!), 100);
              }}
            />
            {selectedSize && (
              <StyledMerchantInfo
                availability={selectedSize.availability}
                productId={productId!}
                sizeId={selectedSize.sizeId}
              />
            )}
          </>
        }
      >
        <StyledGoBack
          productId={productId!}
          data-testid="overlay"
          View={GoBackOverlayView}
          addFirstProductId
        />
        <DiscountBanner reducedSize={true} showHeroNotification />
        <UnlockCouponSuccessHint />
        {isCouponsEnabled && (
          <StyledProminentBannerSectionMinimal
            couponVariant={'coupon-sticky-bar'}
            selectedSize={selectedSize}
            hideCampaignCountdown
            productId={productId!}
          />
        )}
      </MiniADPDialog>
      <CategoryStreamBackground />

      <BasketFlyout
        freeDeliverySection={freeDeliverySectionData}
        statusSuccessSections={statusSuccessSections ?? []}
      />
    </StrictMode>
  );
};

const CloseButtonIcon = () => <Icon asset={GenericIconName.Close} size="S" />;

const StyledMerchantInfo = styled(MemoMerchantInfo)`
  margin-top: 8px;
`;

const StyledGoBack = styled(MemoGoBack)`
  position: absolute;
  right: 0;
  top: 0.4rem;
`;

const GoBackOverlayView = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
`;

const StyledProminentBannerSectionMinimal = styled(
  MemoProminentBannerSectionMinimal,
)`
  &&& {
    position: absolute;
  }
`;
